import {
  PencilSquareIcon,
  PhotoIcon,
  SparklesIcon,
  SpeakerWaveIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { Handle, Position } from '@xyflow/react';

import AudioPlayer from '../../../common/AudioPlayer';
import CanvasPreviewImage from './CanvasPreviewImage';
import type { AppliedMediaNodeData, ClipImageTarget, ClipPreviewEditorTarget } from '../types';

interface AppliedMediaNodeProps {
  data: AppliedMediaNodeData;
}

function isEditableImageFrame(target: AppliedMediaNodeData['target']): target is ClipImageTarget {
  return target === 'start' || target === 'end' || target === 'storyboard';
}

function isPreviewEditorTarget(target: AppliedMediaNodeData['target']): target is ClipPreviewEditorTarget {
  return target === 'audio' || target === 'effects' || target === 'video';
}

export default function AppliedMediaNode({ data }: AppliedMediaNodeProps) {
  const isImage = data.mediaType === 'image';
  const isAudio = data.mediaType === 'audio';
  const canEditImageFrame = isEditableImageFrame(data.target);
  const hasContent = Boolean(data.url || data.summary);
  const canUseAsReference = (isImage || isAudio) && Boolean(data.url);
  const referenceHandleId = isImage ? 'reference:image' : isAudio ? 'reference:audio' : undefined;
  const referenceHandleClass = isImage
    ? 'clip-canvas-media-node__handle clip-canvas-media-node__handle--image'
    : isAudio
      ? 'clip-canvas-media-node__handle clip-canvas-media-node__handle--audio'
      : undefined;
  const canEditPreviewPanel = isPreviewEditorTarget(data.target);
  const canEdit = canEditImageFrame || canEditPreviewPanel;
  const EmptyIcon =
    data.mediaType === 'audio' ? SpeakerWaveIcon :
      data.mediaType === 'effects' ? SparklesIcon :
        data.mediaType === 'video' ? VideoCameraIcon :
          PhotoIcon;

  return (
    <div className="clip-canvas-node clip-canvas-node--media">
      <Handle type="target" position={Position.Left} />
      {canUseAsReference && referenceHandleId && (
        <Handle
          id={referenceHandleId}
          type="source"
          position={Position.Right}
          className={referenceHandleClass}
          title={isAudio ? '音频输出' : '图片输出'}
        />
      )}
      <div className="clip-canvas-node__topline clip-canvas-node__topline--media">
        <div className="clip-canvas-node__header clip-canvas-node__header--inline">
          {data.mediaType === 'audio' ? (
            <SpeakerWaveIcon className="clip-canvas-node__icon" />
          ) : data.mediaType === 'effects' ? (
            <SparklesIcon className="clip-canvas-node__icon" />
          ) : data.mediaType === 'video' ? (
            <VideoCameraIcon className="clip-canvas-node__icon" />
          ) : (
            <PhotoIcon className="clip-canvas-node__icon" />
          )}
          <span>{data.label}</span>
        </div>
        {canEdit && (
          <button
            type="button"
            className="clip-canvas-node__icon-button nodrag nopan"
            onClick={(event) => {
              event.stopPropagation();
              if (isEditableImageFrame(data.target)) {
                data.actions.onEditImageFrame?.(data.target);
              } else if (isPreviewEditorTarget(data.target)) {
                data.actions.onEditPreviewPanel?.(data.target);
              }
            }}
            aria-label={`编辑${data.label}`}
            title={`编辑${data.label}`}
          >
            <PencilSquareIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className={`clip-canvas-preview clip-canvas-preview--${data.mediaType}`}>
        {data.mediaType === 'video' && data.url ? (
          <video src={data.url} className="clip-canvas-preview__media" controls preload="metadata" />
        ) : data.mediaType === 'image' && data.url ? (
          <CanvasPreviewImage
            src={data.url}
            alt={data.label}
            imageClassName="clip-canvas-preview__media"
          />
        ) : data.mediaType === 'audio' && data.url ? (
          <div className="clip-canvas-preview__audio nodrag nopan nowheel">
            <AudioPlayer src={data.url} className="w-full h-8" />
          </div>
        ) : data.mediaType === 'effects' && hasContent ? (
          <div className="clip-canvas-preview__audio">
            <SparklesIcon className="h-8 w-8" />
            <span>{data.summary || '已配置特效'}</span>
          </div>
        ) : (
          <div className="clip-canvas-preview__empty">
            <EmptyIcon className="h-8 w-8" />
            <span>暂无{data.label}</span>
          </div>
        )}
      </div>
      {data.summary && data.mediaType !== 'effects' && (
        <div className="clip-canvas-node__text">{data.summary}</div>
      )}
    </div>
  );
}
